/**
 * ResearchTranscript — the scrolling turn list.
 *
 * Owns three scroll behaviours and nothing else:
 *   • bring a FRESHLY ANSWERED turn's top into view, once, so the reader
 *     starts at the first line of the answer and reads down as it reveals;
 *   • otherwise follow the newest turn while the user is at the bottom;
 *   • page older turns in at the top, restoring the anchor so prepending
 *     leaves the reader exactly where they were.
 *
 * The first of those beats the second deliberately. Pinning to the bottom is
 * right while a turn is running — the working line and the narration are the
 * newest thing — and wrong the moment the answer lands, because the bottom of
 * a long answer is its Sources list. The reader was being shown the end of
 * something they had not been given the start of.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiArrowDownLight } from 'react-icons/pi';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import { CitationOpenRequest } from '../types';
import { ResearchTurn, isAssistantTurn, orderTurns } from '../turns';
import { Artifact } from '../artifacts';
import ResearchTurnView from './ResearchTurnView';
import ClarifyCard, { ClarifyLocator } from './ClarifyCard';

/** Breathing room above a landing answer, so its first line is not flush. */
const TOP_INSET = 12;

interface Props {
  turns: ResearchTurn[];
  isLoadingHistory: boolean;
  isLoadingOlder: boolean;
  hasMoreHistory: boolean;
  onLoadOlder: () => void;
  onHydrate: (messageUuid: string) => void;
  onOpenCitation: (url: string) => void;
  onOpenCitationRequest?: (request: CitationOpenRequest) => void;
  /** Document-shaped turns open in the pane; the transcript shows a card. */
  artifacts?: Artifact[];
  openArtifactTurnId?: string | null;
  onOpenArtifact?: (turnId: string) => void;
  /** Send a suggested follow-up question from a turn. */
  onFollowUp?: (text: string) => void;
  /** Open the ingested copy of a file the user attached. */
  onOpenAttachment?: (citation: any) => void;
  onRerun?: (messageUuid: string, query?: string) => void;
  onSelectVersion?: (messageUuid: string, version: number) => void;
  onRate?: (messageUuid: string, rating: 'up' | 'down' | null) => void;
  onRefine?: (messageUuid: string, instruction?: string) => void;
  /** Load a turn's question into the main composer. */
  onEdit?: (messageUuid: string, query: string) => void;
  /**
   * CLARIFY GATE — answer an outstanding question, continuing the SAME turn.
   *
   * The card renders in the turn's own position rather than at the bottom,
   * because that is what a resumed conversation looks like: the question is
   * still sitting where it was asked.
   */
  onAnswerClarification?: (messageUuid: string, answers: Record<string, string>) => void;
  /** Give up on an unanswered question. Not "stop" — nothing is running. */
  onAbandonClarification?: (messageUuid: string) => void;
  /**
   * The clarify card's `attach_document` action — upload to THIS chat and
   * return the locator the answer carries. Absent, the action is not offered.
   */
  onClarifyAttach?: (files: File[]) => Promise<ClarifyLocator | null>;
  /** The clarify card's `link_matter` action. */
  onClarifyLinkMatter?: () => void;
  /**
   * NARROW MODE — the transcript inside a 360–900px pane.
   *
   * Drops the centred reading column (which at pane width is the pane) and the
   * page-scale gutters, so the turns get the width they have rather than
   * spending a fifth of it on padding. The turn views themselves are already
   * width-agnostic — their `max-w-*` are ceilings, not widths — so nothing
   * below this changes.
   */
  compact?: boolean;
}

const ResearchTranscript: React.FC<Props> = ({
  turns,
  isLoadingHistory,
  isLoadingOlder,
  hasMoreHistory,
  onLoadOlder,
  onHydrate,
  onOpenCitation,
  onOpenCitationRequest,
  artifacts = [],
  openArtifactTurnId,
  onOpenArtifact,
  onFollowUp,
  onOpenAttachment,
  onRerun,
  onSelectVersion,
  onRate,
  onRefine,
  onEdit,
  onAnswerClarification,
  onAbandonClarification,
  onClarifyAttach,
  onClarifyLinkMatter,
  compact = false,
}) => {
  const { t } = useTranslation();
  const column = compact ? '' : 'max-w-3xl mx-auto';
  const listRef = useRef<HTMLDivElement | null>(null);
  const topSentinelRef = useRef<HTMLDivElement | null>(null);
  /** Distance from the bottom, captured before an older page is prepended. */
  const anchorRef = useRef<number | null>(null);
  /** Turns already scrolled to. A turn is brought into view exactly once. */
  const landedRef = useRef<Set<string>>(new Set());
  const [atBottom, setAtBottom] = useState(true);

  /**
   * The turn whose answer just arrived, live, and has not been scrolled to.
   *
   * `liveArrival` is the same flag the reveal reads: an answer read back from
   * history has none, so reopening a chat never moves the reader. A version
   * swap is excluded — that answer is a fetch, and the reader is already
   * looking at the place it lands.
   */
  const landing = orderTurns(turns).find(
    (turn) =>
      isAssistantTurn(turn) &&
      !!turn.liveArrival &&
      !!turn.response &&
      !turn.versionLoading &&
      !landedRef.current.has(turn.id)
  );

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;

    // Restoring the anchor wins over everything: prepending older turns must
    // not move the reader at all.
    if (anchorRef.current !== null) {
      list.scrollTop = list.scrollHeight - anchorRef.current;
      anchorRef.current = null;
      return;
    }

    /*
      A NEW ANSWER: its first line, not its last.

      Once per turn, and smoothly — the reveal is running underneath, and a
      jump would undo the point of it. Nothing re-fires afterwards, so a
      reader who scrolls away mid-reveal is left where they put themselves.
    */
    if (landing) {
      landedRef.current.add(landing.id);
      const node = list.querySelector<HTMLElement>(`[data-turn-id="${landing.id}"]`);
      if (node) {
        const top =
          node.getBoundingClientRect().top -
          list.getBoundingClientRect().top +
          list.scrollTop -
          TOP_INSET;
        const target = Math.max(0, Math.round(top));
        // jsdom, and any container mid-teardown, has no smooth scrolling.
        if (typeof list.scrollTo === 'function') {
          list.scrollTo({ top: target, behavior: 'smooth' });
        } else {
          list.scrollTop = target;
        }
        // The reader is now at the answer's head, not the transcript's foot:
        // the follow-the-bottom rule must not drag them back down as the
        // reveal grows the page.
        setAtBottom(false);
        return;
      }
    }

    if (atBottom) list.scrollTop = list.scrollHeight;
  }, [turns, atBottom, landing]);

  useEffect(() => {
    const sentinel = topSentinelRef.current;
    if (!sentinel || !hasMoreHistory) return;
    if (typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting || isLoadingOlder) return;
        const list = listRef.current;
        if (list) anchorRef.current = list.scrollHeight - list.scrollTop;
        onLoadOlder();
      },
      { root: listRef.current, rootMargin: '200px 0px 0px 0px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMoreHistory, isLoadingOlder, onLoadOlder]);

  const handleScroll = () => {
    const list = listRef.current;
    if (!list) return;
    const { scrollTop, scrollHeight, clientHeight } = list;
    setAtBottom(scrollTop + clientHeight >= scrollHeight - 24);
  };

  if (isLoadingHistory && turns.length === 0) {
    return (
      <div
        className={`flex-1 overflow-y-auto ${compact ? 'px-2 pt-3' : 'px-4 pt-6'}`}
        data-testid="transcript-loading"
      >
        <div className={`${column} space-y-4`}>
          <Skeleton variant="card" rows={3} />
          <Skeleton variant="card" rows={2} />
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex-1 min-h-0">
      <div
        ref={listRef}
        onScroll={handleScroll}
        className={`h-full overflow-y-auto ${compact ? 'px-2 pt-3 pb-4' : 'px-4 pt-6 pb-8'}`}
        data-testid="transcript"
        data-compact={compact}
      >
        <div className={column}>
          {hasMoreHistory && (
            <div
              ref={topSentinelRef}
              data-testid="history-sentinel"
              className="py-2 flex justify-center"
            >
              {isLoadingOlder && (
                <span className="text-xs text-fg-muted">{t('Loading earlier messages…')}</span>
              )}
            </div>
          )}

          {orderTurns(turns).map((turn, index, ordered) => {
            const artifact = artifacts.find((a) => a.turnId === turn.id);
            // The reply to a user turn is the turn after it; its citations
            // are where an uploaded file's openable copy lives.
            const reply = turn.kind === 'user' ? ordered[index + 1] : undefined;
            const answerCitations =
              reply && isAssistantTurn(reply) ? reply.response?.citations : undefined;
            /**
             * An OPEN QUESTION renders instead of the answer view, not beside
             * it. There is no answer: nothing was enqueued, nothing was
             * metered, and the run has not started. Dispatched here rather
             * than inside the turn view so the answer renderer keeps exactly
             * one job.
             */
            if (
              isAssistantTurn(turn) &&
              turn.clarification &&
              !turn.clarification.settled &&
              turn.messageUuid &&
              onAnswerClarification
            ) {
              return (
                <div key={turn.id} className="mb-6">
                  <ClarifyCard
                    messageUuid={turn.messageUuid}
                    clarification={turn.clarification}
                    onAnswer={onAnswerClarification}
                    onCancel={onAbandonClarification}
                    onAttachDocument={onClarifyAttach}
                    onLinkMatter={onClarifyLinkMatter}
                    compact={compact}
                  />
                </div>
              );
            }
            return (
              <ResearchTurnView
                key={turn.id}
                turn={turn}
                onHydrate={onHydrate}
                onOpenCitation={onOpenCitation}
                onOpenCitationRequest={onOpenCitationRequest}
                onOpenArtifact={onOpenArtifact}
                onFollowUp={onFollowUp}
                answerCitations={answerCitations}
                onOpenAttachment={onOpenAttachment}
                onRerun={onRerun}
                onSelectVersion={onSelectVersion}
                onRate={onRate}
                onRefine={onRefine}
                onEdit={onEdit}
                isArtifactOpen={!!artifact && artifact.turnId === openArtifactTurnId}
                artifactVersion={artifact?.version}
                artifactVersionCount={artifact?.versionCount}
              />
            );
          })}
        </div>
      </div>

      {!atBottom && (
        <button
          type="button"
          onClick={() => {
            const list = listRef.current;
            if (list) list.scrollTo({ top: list.scrollHeight, behavior: 'smooth' });
            setAtBottom(true);
          }}
          aria-label={t('Scroll to latest')}
          className="absolute bottom-4 right-4 glass p-2 rounded-full text-fg-body
                     hover:text-champagne transition-all duration-200 ease-slide z-10"
        >
          <PiArrowDownLight className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export default ResearchTranscript;
